import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../api/api";
import { setLoading } from "../../loading/loadingSlice";

const REQUEST_SORT = "-createdAt";

const buildParams = (statuses, page, limit, sort = REQUEST_SORT) => {
  const params = new URLSearchParams();
  statuses.forEach((s) => params.append("status", s));
  params.append("page", String(page));
  params.append("limit", String(limit));
  params.append("sort", sort);
  return params;
};

export const getNurseRequests = createAsyncThunk("manageRequest/getRequests", async ({ tab, statuses, page = 1, limit = 10, countOnly = false, silent = false }, { dispatch }) => {
  try {
    if (!countOnly && !silent) dispatch(setLoading(true));

    const response = await api.get("/api/v1/cc/nursing_care/request", {
      params: buildParams(statuses, page, limit),
      headers: { "Cache-Control": "no-cache" },
    });

    return { tab, countOnly, ...response.data };
  } catch (error) {
    const errorMessage = error.response?.data?.message || "Error fetching requests";
    throw new Error(errorMessage);
  } finally {
    if (!countOnly && !silent) dispatch(setLoading(false));
  }
});

export const getNurseSessions = createAsyncThunk("manageRequest/getSessions", async ({ tab, statuses, page = 1, limit = 10, countOnly = false, silent = false }, { dispatch }) => {
  try {
    if (!countOnly && !silent) dispatch(setLoading(true));

    const response = await api.get("/api/v1/cc/nursing_care/request/session", {
      params: buildParams(statuses, page, limit),
      headers: { "Cache-Control": "no-cache" },
    });

    return { tab, countOnly, ...response.data };
  } catch (error) {
    const errorMessage = error.response?.data?.message || "Error fetching sessions";
    throw new Error(errorMessage);
  } finally {
    if (!countOnly && !silent) dispatch(setLoading(false));
  }
});

export const getNurseRequestDetails = createAsyncThunk("manageRequest/getRequestDetails", async ({ id }, { dispatch }) => {
  try {
    dispatch(setLoading(true));

    const response = await api.get(`/api/v1/cc/nursing_care/request/${id}`, {
      headers: { "Cache-Control": "no-cache" },
    });
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || "Error fetching request details";
    throw new Error(errorMessage);
  } finally {
    dispatch(setLoading(false));
  }
});

export const fetchRequestById = createAsyncThunk("manageRequest/fetchRequestById", async ({ id }) => {
  try {
    const response = await api.get(`/api/v1/cc/nursing_care/request/${id}`, {
      headers: { "Cache-Control": "no-cache" },
    });
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || "Error fetching request details";
    throw new Error(errorMessage);
  }
});

export const verifyNurseRequest = createAsyncThunk("manageRequest/verifyRequest", async ({ id, body }, { dispatch }) => {
  try {
    dispatch(setLoading(true));

    const response = await api.put(`/api/v1/cc/nursing_care/request/${id}`, body, {
      headers: { "Cache-Control": "no-cache" },
    });

    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || "Error verifying request";
    throw new Error(errorMessage);
  } finally {
    dispatch(setLoading(false));
  }
});

export const getServiceItemsBySubService = createAsyncThunk("manageRequest/getServiceItems", async (subServiceId, { dispatch }) => {
  try {
    dispatch(setLoading(true));
    const response = await api.get(`/api/v1/cc/nursing_care/service/service_item`, {
      params: { subService: subServiceId, page: 1, limit: 100 },
      headers: { "Cache-Control": "no-cache" },
    });
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || "Error fetching service items";
    throw new Error(errorMessage);
  } finally {
    dispatch(setLoading(false));
  }
});

export const getNurseSessionDetails = createAsyncThunk("manageRequest/getSessionDetails", async ({ id }, { dispatch }) => {
  try {
    dispatch(setLoading(true));

    const response = await api.get(`/api/v1/cc/nursing_care/request/session/${id}`, {
      headers: { "Cache-Control": "no-cache" },
    });

    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || "Error fetching session details";
    throw new Error(errorMessage);
  } finally {
    dispatch(setLoading(false));
  }
});

// GET /api/v1/cc/nursing_care/request/sessions/:requestId
// Returns all sessions for a specific nursing request
export const getRequestSessions = createAsyncThunk("manageRequest/getRequestSessions", async ({ requestId }, { dispatch }) => {
  try {
    dispatch(setLoading(true));
    const response = await api.get(`/api/v1/cc/nursing_care/request/sessions/${requestId}`, {
      headers: { "Cache-Control": "no-cache" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error fetching request sessions");
  } finally {
    dispatch(setLoading(false));
  }
});

// GET /api/v1/cc/nursing_care/request/session/:sessionId/nurse
// Returns available nurses for a specific session
export const getSessionNurses = createAsyncThunk("manageRequest/getSessionNurses", async ({ sessionId }) => {
  try {
    const response = await api.get(`/api/v1/cc/nursing_care/request/session/${sessionId}/nurse`, {
      headers: { "Cache-Control": "no-cache" },
    });
    return { sessionId, nurses: response.data?.data?.nurses || [] };
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error fetching session nurses");
  }
});

// GET /api/v1/cc/nursing_care/patient_history?hnNumber=xxx
// Returns patient profile + address + all sessions for a given HN number
export const getPatientHistory = createAsyncThunk("manageRequest/getPatientHistory", async ({ hnNumber }, { dispatch }) => {
  try {
    dispatch(setLoading(true));
    const response = await api.get("/api/v1/cc/nursing_care/patient_history", {
      params: { hnNumber },
      headers: { "Cache-Control": "no-cache" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Patient not found");
  } finally {
    dispatch(setLoading(false));
  }
});

// GET /api/v1/cc/nursing_care/specimen
// Returns paginated specimen collection logs
export const getSpecimenLogs = createAsyncThunk("manageRequest/getSpecimenLogs", async ({ page = 1, limit = 10 } = {}, { dispatch }) => {
  try {
    dispatch(setLoading(true));
    const response = await api.get("/api/v1/cc/nursing_care/specimen", {
      params: { page, limit },
      headers: { "Cache-Control": "no-cache" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error fetching specimen logs");
  } finally {
    dispatch(setLoading(false));
  }
});

// GET /api/v1/cc/nursing_care/request/session/:sessionId
// Lightweight fetch — returns data only, does NOT write to Redux sessionDetails
// Silent — no global loading spinner, used for batch nurse name lookups
export const fetchSessionById = createAsyncThunk("manageRequest/fetchSessionById", async ({ sessionId }) => {
  try {
    const response = await api.get(`/api/v1/cc/nursing_care/request/session/${sessionId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error fetching session");
  }
});

// POST /api/v1/cc/nursing_care/request/:id  — Assign request (step 1 Assign button)
export const assignNurseRequest = createAsyncThunk("manageRequest/assignRequest", async ({ id, body = {} }, { dispatch }) => {
  try {
    dispatch(setLoading(true));
    const response = await api.post(`/api/v1/cc/nursing_care/request/${id}`, body, {
      headers: { "Cache-Control": "no-cache" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error assigning request");
  } finally {
    dispatch(setLoading(false));
  }
});

// POST /api/v1/cc/nursing_care/request/schedule/:requestId
// Confirms / schedules a nursing request
export const scheduleNursingRequest = createAsyncThunk("manageRequest/scheduleRequest", async ({ requestId, body = {} }, { dispatch }) => {
  try {
    dispatch(setLoading(true));
    const response = await api.put(`/api/v1/cc/nursing_care/request/schedule/${requestId}`, body, {
      headers: { "Cache-Control": "no-cache" },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error scheduling request");
  } finally {
    dispatch(setLoading(false));
  }
});

// POST /api/v1/cc/nursing_care/request/session/:sessionId
// Assigns nurse(s) to a session. Backend expects nurseIDs as an array.
// Response: { status: "success", data: { assigned: true } }
export const assignSessionNurse = createAsyncThunk("manageRequest/assignSessionNurse", async (payload, { dispatch }) => {
  const { sessionId, nurseId, nursingInstructions } = payload;
  const body = { nurseIDs: [nurseId] };
  if (nursingInstructions !== undefined && nursingInstructions !== "") {
    body.nurseInstruction = nursingInstructions;
  }

  try {
    dispatch(setLoading(true));
    const response = await api.post(
      `/api/v1/cc/nursing_care/request/session/${sessionId}`,
      body,
      { headers: { "Cache-Control": "no-cache" } },
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error assigning nurse to session");
  } finally {
    dispatch(setLoading(false));
  }
});
